import { useRef, useState, type PointerEvent as RPointerEvent } from 'react';
import { Icon } from './Icon';

export interface OrderEvent { id: string; label: string }

const move = (arr: string[], from: number, to: number) => { const a = arr.slice(); const [it] = a.splice(from, 1); a.splice(to, 0, it); return a; };

interface DragState { id: string; from: number; to: number; startY: number; dy: number; mids: number[]; pitch: number }

/**
 * Ordering list (earliest at the top). Two input methods:
 *  - drag the numbered handle (pointer events: touch, pen and mouse);
 *  - tap the arrow buttons (always available, keyboard accessible).
 * The list is controlled: `order` comes from the phone's local draft, `onChange` reports a new order.
 */
export function OrderingList({ events, order, onChange, locked }: { events: OrderEvent[]; order: string[]; onChange?: (order: string[]) => void; locked?: boolean }) {
  const labels = Object.fromEntries(events.map((e) => [e.id, e.label]));
  const refs = useRef<Record<string, HTMLDivElement | null>>({});
  const [drag, setDrag] = useState<DragState | null>(null);
  const editable = !locked && !!onChange;

  const onDown = (e: RPointerEvent<HTMLSpanElement>, id: string, from: number) => {
    if (!editable) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    const rects = order.map((x) => refs.current[x]!.getBoundingClientRect());
    const mids = rects.map((r) => r.top + r.height / 2);
    const pitch = rects[from].height + 8; // others shift by the dragged card's height plus the list gap
    setDrag({ id, from, to: from, startY: e.clientY, dy: 0, mids, pitch });
  };
  const onMove = (e: RPointerEvent<HTMLSpanElement>) => {
    if (!drag || !editable) return;
    const dy = e.clientY - drag.startY;
    const centre = drag.mids[drag.from] + dy;
    let to = 0;
    drag.mids.forEach((m, i) => { if (i !== drag.from && m < centre) to++; });
    setDrag({ ...drag, dy, to });
  };
  const onUp = () => {
    if (!drag) return;
    if (editable && drag.to !== drag.from) onChange?.(move(order, drag.from, drag.to));
    setDrag(null);
  };

  return (
    <div className="ex-order" role="list" aria-label="Events, earliest first">
      <div className="ex-order__end"><Icon name="up" />Earliest</div>
      {order.map((id, i) => {
        let shift = 0;
        if (drag && id !== drag.id) {
          if (drag.from < drag.to && i > drag.from && i <= drag.to) shift = -drag.pitch;
          if (drag.to < drag.from && i >= drag.to && i < drag.from) shift = drag.pitch;
        }
        const dragging = drag?.id === id;
        const state = locked ? 'locked' : dragging ? 'dragging' : 'default';
        return (
          <div key={id} ref={(el) => { refs.current[id] = el; }} role="listitem" className={`ex-ocard${drag && !dragging ? ' ex-ocard--shift' : ''}`} data-state={state}
            aria-label={`Position ${i + 1}: ${labels[id]}`}
            style={{ transform: dragging ? `translateY(${drag!.dy}px) scale(1.02)` : shift ? `translateY(${shift}px)` : undefined }}>
            <span className="ex-ocard__pos" data-handle={editable} onPointerDown={(e) => onDown(e, id, i)} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={() => setDrag(null)}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, width: editable ? 48 : 40 }}
              aria-hidden="true">
              {i + 1}{editable && <Icon name="grip" size={16} style={{ opacity: 0.7 }} />}
            </span>
            <span className="ex-ocard__text">{labels[id]}</span>
            {editable && (
              <span className="ex-ocard__moves">
                <button type="button" className="ex-ocard__move" aria-label={`Move earlier: ${labels[id]}`} disabled={i === 0} onClick={() => onChange!(move(order, i, i - 1))}><Icon name="up" /></button>
                <button type="button" className="ex-ocard__move" aria-label={`Move later: ${labels[id]}`} disabled={i === order.length - 1} onClick={() => onChange!(move(order, i, i + 1))} style={{ borderTop: '1px solid rgba(14,20,48,.16)' }}><Icon name="down" /></button>
              </span>
            )}
          </div>
        );
      })}
      <div className="ex-order__end"><Icon name="down" />Latest</div>
    </div>
  );
}

/**
 * Reveal: cards slide from the player's order into the correct order, with dates.
 * `mine` = the accepted order (or null if unanswered). Pair-based scoring is shown by the verdict, so
 * cards only mark "same place as yours" in teal; others stay neutral with "You put it #n".
 */
export function OrderingReveal({ correct, mine, initial, pitch = 80, size = 'phone' }:
  { correct: { id: string; label: string; date: string }[]; mine: string[] | null; initial: string[]; pitch?: number; size?: 'phone' | 'tv' }) {
  if (size === 'tv') {
    const N = correct.length, colW = (1728 - 24 * (N - 1)) / N + 24;
    return (
      <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: `repeat(${N}, minmax(0,1fr))`, gap: 24 }}>
        <div className="mo-wipe" aria-hidden="true" style={{ ['--d' as string]: '1500ms', position: 'absolute', left: 0, right: 0, bottom: -34, height: 6, borderRadius: 3, background: '#FFCC00' }} />
        {correct.map((c, i) => {
          const from = initial.indexOf(c.id);
          return (
            <div key={c.id} className="ex-ocard mo-slidex" data-state="correct"
              style={{ ['--dx' as string]: `${Math.round((from - i) * colW)}px`, ['--rot' as string]: `${[-2, 1.5, -1, 2, -1.5][from % 5]}deg`, ['--d' as string]: `${300 + i * 160}ms`, flexDirection: 'column', minHeight: 330 }}>
              <span className="ex-ocard__pos" style={{ width: '100%', height: 64, fontSize: 30 }}>{['I', 'II', 'III', 'IV', 'V', 'VI'][i]}</span>
              <span className="ex-ocard__text" style={{ fontSize: 31, lineHeight: 1.22, padding: 22, alignSelf: 'stretch' }}>{c.label}</span>
              <span className="ex-display mo-fade" style={{ ['--d' as string]: `${1500 + i * 180}ms`, fontSize: 64, color: '#7A5800', padding: '0 22px 18px' }}>{c.date}</span>
            </div>
          );
        })}
      </div>
    );
  }
  return (
    <div className="ex-order" role="list" aria-label="Correct order, earliest first">
      <div className="ex-order__end"><Icon name="up" />Earliest · correct order</div>
      {correct.map((c, i) => {
        const yi = mine ? mine.indexOf(c.id) : initial.indexOf(c.id);
        const same = !!mine && mine[i] === c.id;
        return (
          <div key={c.id} className="ex-ocard mo-reorder" role="listitem" data-state={same ? 'correct' : 'locked'}
            style={{ ['--dy' as string]: `${(yi - i) * pitch}px`, ['--d' as string]: `${500 + i * 140}ms` }}>
            <span className="ex-ocard__pos" aria-hidden="true">{i + 1}</span>
            <span className="ex-ocard__text">{c.label}<span className="ex-ocard__date">{c.date} · {mine ? `You put it #${yi + 1}` : 'Not answered'}</span></span>
            <span style={{ flex: 'none', width: 44, display: 'grid', placeItems: 'center', fontSize: 22 }}>
              <Icon name={same ? 'check' : 'same'} label={same ? 'Same place as yours' : 'Different place'} style={{ opacity: same ? 1 : 0.35 }} />
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** TV question view: shuffled cards, slightly scattered, not interactive. */
export function OrderingBoardTV({ events, locked }: { events: OrderEvent[]; locked?: boolean }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${events.length}, minmax(0,1fr))`, gap: 24 }}>
      {events.map((e, i) => (
        <div key={e.id} style={{ transform: `rotate(${[-2, 1.5, -1, 2, -1.5][i % 5]}deg)`, display: 'flex' }}>
          <div className="ex-ocard mo-rise" data-state={locked ? 'locked' : 'default'} style={{ ['--d' as string]: `${200 + i * 120}ms`, flex: 1, flexDirection: 'column', minHeight: 330 }}>
            <span className="ex-ocard__pos" style={{ width: '100%', height: 64, fontSize: 30 }}>{'ABCDEF'[i]}</span>
            <span className="ex-ocard__text" style={{ fontSize: 31, lineHeight: 1.22, padding: 22, alignSelf: 'stretch' }}>{e.label}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
